import { useState } from 'react';
import { useCambiosSinGuardar, useSubpantalla } from '../ui/navegacionReact';
import {
  Package, HardHat, ArrowLeft, Check, Loader2, Sparkles, MapPin,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { vocabulario } from '../ui/vocabulario';

// REGISTRAR ALGO EN CASA, EN DOS PANTALLAS (Bloque L, fase L-2).
//
// EL MURO QUE QUITA, y estaba medido: se puede dar de alta un producto sin
// ubicación, pero registrar que LO TIENES exige `ubicacion_destino`, y una
// cuenta recién creada tiene cero ubicaciones. Es decir: quien estrenaba
// una cuenta personal tenía que entender qué es una «ubicación» y crear
// una antes de poder apuntar su primer taladro. Ahí se abandona.
//
// LA SOLUCIÓN NO ES DEBILITAR EL MODELO. La ubicación se sigue exigiendo
// —el kárdex sin sitio no vale nada— pero se crea sola la primera vez, con
// el nombre que la persona escriba o «Mi casa» si no escribe ninguno. El
// concepto sigue existiendo en la base; lo que desaparece es la obligación
// de aprenderlo para empezar.
//
// EL SKU TAMPOCO SE PREGUNTA. Es obligatorio y único en el modelo, así que
// se genera. Pedirle un «código único de producto» a alguien que quiere
// apuntar unas pilas es la definición de jerga.

const LUGAR_POR_OMISION = 'Mi casa';

// Un código legible a partir del nombre, con una cola corta para que dos
// cosas parecidas no choquen. No se le enseña a nadie: vive en la ficha.
function codigoDesde(nombre) {
  const base = nombre.normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 20);
  const cola = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${base || 'COSA'}-${cola}`;
}

export default function PrimeraCosa({ irA }) {
  const { esEmpresa } = useAuth();
  const palabras = vocabulario(esEmpresa);
  const [tipo, setTipo] = useState(null);       // 'VENTA' | 'ACTIVO'
  // Atrás vuelve a elegir qué se registra, igual que «Cambiar»; lo escrito se conserva.
  useSubpantalla('tipo', !!tipo, () => setTipo(null), { valor: tipo, abrir: setTipo });
  const [nombre, setNombre] = useState('');
  const [cuantas, setCuantas] = useState('1');
  const [lugar, setLugar] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  // Registro a medias (TC-DR-AT): con el nombre o el lugar escritos y sin
  // guardar, salir de la pantalla pregunta. «Cambiar» y atrás dentro de
  // ella no preguntan: vuelven a la elección y lo escrito se conserva.
  useCambiosSinGuardar(() => !hecho && (nombre.trim() !== '' || lugar.trim() !== ''));

  // Se asegura de que exista un sitio donde guardarlo. Si la persona
  // escribió uno, ese; si no, «Mi casa». Reutiliza el que ya haya para no
  // llenar la lista de lugares repetidos.
  async function asegurarLugar(nombreLugar) {
    const existentes = await api.get('/ubicaciones?limite=200').catch(() => []);
    const escrito = (nombreLugar || '').trim();

    // SI NO ESCRIBIO NINGUNO Y YA HAY UN SITIO, SE USA ESE. Toda cuenta
    // nace con una ubicacion inicial —la crea `crear-dueno`— asi que
    // inventar «Mi casa» al lado dejaria dos lugares el primer dia, uno
    // vacio y otro con todo. Solo se inventa cuando de verdad no hay
    // ninguno.
    if (!escrito && existentes.length > 0) return existentes[0].codigo;

    const buscado = (escrito || LUGAR_POR_OMISION).toLowerCase();
    const ya = existentes.find((u) => (u.nombre || '').trim().toLowerCase() === buscado);
    if (ya) return ya.codigo;

    const codigo = codigoDesde(nombreLugar || LUGAR_POR_OMISION);
    const creado = await api.post('/ubicaciones', {
      codigo, nombre: (nombreLugar || LUGAR_POR_OMISION).trim(), tipo: 'ALMACEN',
    });
    return creado.codigo;
  }

  async function guardar(e) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      const sku = codigoDesde(nombre);
      await api.post('/productos', {
        sku,
        nombre: nombre.trim(),
        tipo_inventario: tipo,
        unidad_medida: 'PIEZA',
      });

      const codigoLugar = await asegurarLugar(lugar);
      const cantidad = Number(cuantas) || 1;
      await api.post('/inventario/movimientos', {
        tipo: 'ENTRADA', sku, cantidad, ubicacion_destino: codigoLugar,
      });

      setHecho({ nombre: nombre.trim(), cantidad, lugar: lugar.trim() || LUGAR_POR_OMISION, tipo });
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message
        : 'No se pudo guardar. Inténtalo otra vez.');
      setGuardando(false);
    }
  }

  function otra() {
    setTipo(null); setNombre(''); setCuantas('1'); setLugar('');
    setHecho(null); setGuardando(false); setError(null);
  }

  // --- Ya está: la confirmación ---------------------------------------
  if (hecho) {
    const destino = hecho.tipo === 'ACTIVO' ? 'activos' : 'productos';
    return (
      <div className="max-w-md mx-auto text-center space-y-4 py-6">
        <div className="inline-flex bg-green-100 text-green-700 rounded-2xl p-4">
          <Check className="h-8 w-8" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Listo</h2>
          <p className="text-sm text-gray-600 mt-1">
            Apunté <strong>{hecho.cantidad}</strong> de «{hecho.nombre}» en{' '}
            <strong>{hecho.lugar}</strong>.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button type="button" onClick={otra}
                  className="bg-[var(--color-marca)] text-white font-semibold rounded-xl py-3">
            Registrar otra cosa
          </button>
          <button type="button" onClick={() => irA(destino, {})}
                  className="text-sm font-semibold text-[var(--color-marca)] underline">
            Ver mis {hecho.tipo === 'ACTIVO'
              ? palabras.activos.toLowerCase() : palabras.productos.toLowerCase()}
          </button>
        </div>
      </div>
    );
  }

  // --- Pantalla 1: qué clase de cosa es --------------------------------
  //
  // LA PREGUNTA ESTÁ EN CRISTIANO, no en «tipo de inventario». Y la
  // respuesta se da tocando una tarjeta grande, no eligiendo de una lista:
  // son dos opciones y van a leerse con el teléfono en una mano.
  if (!tipo) {
    return (
      <div className="max-w-md mx-auto space-y-4">
        <header>
          <h2 className="text-xl font-semibold text-gray-900">Registrar algo</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Empieza por decirme qué clase de cosa es. Puedes cambiarlo después.
          </p>
        </header>

        <button type="button" onClick={() => setTipo('VENTA')}
                className="w-full text-left bg-white border border-gray-200 rounded-xl p-4
                           flex items-start gap-3">
          <div className="bg-[var(--color-marca-suave)] text-[var(--color-marca)] rounded-xl p-3">
            <Package className="h-6 w-6" aria-hidden="true" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">Algo que se acaba</div>
            <div className="text-sm text-gray-600">
              Comida, jabón, pilas, café. TEGO te avisa cuando se está acabando.
            </div>
          </div>
        </button>

        <button type="button" onClick={() => setTipo('ACTIVO')}
                className="w-full text-left bg-white border border-gray-200 rounded-xl p-4
                           flex items-start gap-3">
          <div className="bg-[var(--color-marca-suave)] text-[var(--color-marca)] rounded-xl p-3">
            <HardHat className="h-6 w-6" aria-hidden="true" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">Algo que se queda</div>
            <div className="text-sm text-gray-600">
              El taladro, la bici, la consola. Para saber dónde está y en qué estado.
            </div>
          </div>
        </button>
      </div>
    );
  }

  // --- Pantalla 2: qué es y dónde está --------------------------------
  const listo = nombre.trim().length >= 2;
  return (
    <form onSubmit={guardar} className="max-w-md mx-auto space-y-4">
      <button type="button" onClick={() => setTipo(null)}
              className="inline-flex items-center gap-1 text-sm font-semibold text-gray-600">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Cambiar
      </button>

      <header>
        <h2 className="text-xl font-semibold text-gray-900">
          {tipo === 'VENTA' ? '¿Qué se acaba?' : '¿Qué es?'}
        </h2>
        <p className="text-sm text-gray-600 mt-0.5">
          Con el nombre basta. Lo demás lo puedes cambiar cuando quieras.
        </p>
      </header>

      <div>
        <label className="block text-sm font-medium text-gray-700" htmlFor="nombre">
          Nombre
        </label>
        <input id="nombre" value={nombre} onChange={(e) => setNombre(e.target.value)}
               placeholder={tipo === 'VENTA' ? 'Café molido' : 'Taladro inalámbrico'}
               maxLength={200} autoFocus
               className="mt-1 w-full border border-gray-300 rounded-xl px-4 py-3 text-base" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700" htmlFor="cuantas">
            ¿Cuántas tienes?
          </label>
          <input id="cuantas" value={cuantas} inputMode="numeric"
                 onChange={(e) => setCuantas(e.target.value.replace(/\D/g, ''))}
                 className="mt-1 w-full border border-gray-300 rounded-xl px-4 py-3 text-base" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700" htmlFor="lugar">
            ¿Dónde lo guardas?
          </label>
          <input id="lugar" value={lugar} onChange={(e) => setLugar(e.target.value)}
                 placeholder={LUGAR_POR_OMISION} maxLength={80}
                 className="mt-1 w-full border border-gray-300 rounded-xl px-4 py-3 text-base" />
        </div>
      </div>
      {/* AYUDA EN EL SITIO, no en un manual. El campo del lugar es el que
          más duda genera, y es justamente el que puede dejarse en blanco. */}
      <p className="text-xs text-gray-500 -mt-2 flex items-start gap-1.5">
        <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
        Si lo dejas en blanco lo guardo en «{LUGAR_POR_OMISION}». Puedes usar
        «Cocina», «Bodega», «Cajón de herramientas»… lo que te sirva a ti.
      </p>

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-800 rounded-xl p-3 text-sm">
          {error}
        </div>
      )}

      <button type="submit" disabled={!listo || guardando}
              title={!listo ? 'Escribe primero el nombre' : undefined}
              className="w-full bg-[var(--color-marca)] text-white font-semibold rounded-xl
                         py-4 disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {guardando
          ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          : <Sparkles className="h-5 w-5" aria-hidden="true" />}
        {guardando ? 'Guardando…' : 'Guardar'}
      </button>
      {!listo && (
        <p className="text-xs text-gray-500 -mt-2 text-center">
          Escribe el nombre para poder guardar.
        </p>
      )}
    </form>
  );
}
